import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import {
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbDivider,
  BreadcrumbItem,
  Button,
  __css,
  mergeClasses,
  Menu,
  MenuItem,
  MenuButton,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Overflow,
  OverflowItem,
  Tab,
  TabList,
  tokens,
  Tooltip,
  useIsOverflowItemVisible,
  useOverflowMenu
} from "@fluentui/react-components";
import { Navigation20Regular } from "@fluentui/react-icons";
import { Fragment as Fragment2 } from "react";
import { useTranslation } from "react-i18next";
import { brandTokens } from "../theme/themes.js";
import { layout, typeScale } from "../theme/tokens.js";
import { listPaneIsDrawer } from "./layout.js";
import { useSegmentedTabsStyles } from "./segmented-tabs.js";
import { useShell } from "./app-shell.js";
const useStyles = __css({"header":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":0,"Belr9w4":0,"rmohyg":"fu9asb0","qttt6h":"f1bconio","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f1xzk6ru","rjzwhg":"fqu8h5b","Dbcxam":"f9nj55d","Eh141a":"fni485r","Bnnss6s":"fi64zpg","Bhzhn95":0,"B0czu1p":0,"cglito":"f10fimcf"},"titles":{"Bh6795r":"fqerorx","Bgto7am":"fuzgk9b","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62"},"title":{"jrapky":0,"Frg6f3":0,"t21cq0":0,"B6of3ja":0,"B74szlk":"f1s184ao","Be2twd7":"f1rt2boy","Bg96gwp":"f1dsjtc7","Bhrd7zp":"fl43uef","ublp8o":"f4c95hg","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp","ygn44y":"f1cmbuwj","Huce71":"fz5stix"},"actions":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":0,"Belr9w4":0,"rmohyg":"faqewft","Jyy4pa":"f1lfeew"},"tabs":{"Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f1xzk6ru","Dbcxam":"f9nj55d","Bhzhn95":0,"B0czu1p":0,"cglito":"f10fimcf","Bnnss6s":"fi64zpg","rjzwhg":"f1vm9gt1"},"overflow":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f1ufnopg","Bgto7am":"fuzgk9b","Bns2gvi":"f1c209jx"},"track":{"D305fb":"fd399pk","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp"},"moreButton":{"Bnnss6s":"fi64zpg","qttt6h":"f10161w5","Bgto7am":"f1wjrrhv","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f100d0uy","De3pzq":"f1ac3ntu","Bhrd7zp":"figsok6"},"moreCount":{"mc9l5x":"ftuwxu6","Bt984gj":"f122n59","Brf1p80":"f4d9j23","Bgto7am":"f1cd1ik7","st59p4":"f1m6mkub","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f1rktw8j","B7ck84d":"f1ewtqcl","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f44lkw9","De3pzq":"fxugw4r","Be2twd7":"fy9rknc","Bhrd7zp":"fl43uef"},"content":{"Bh6795r":"fqerorx","qttt6h":"fl4vidq","Bmxbyg5":"f5zp4f"}});
function PaneHeader({ title, breadcrumb, actions }) {
  const styles = useStyles();
  const { t } = useTranslation("ui");
  const { tier, store, hasListPane } = useShell();
  return /* @__PURE__ */ jsxs("div", { className: styles.header, children: [
    hasListPane && listPaneIsDrawer(tier) && /* @__PURE__ */ jsx(Tooltip, { content: t("shell.listPane.open"), relationship: "inaccessible", children: /* @__PURE__ */ jsx(
      Button,
      {
        appearance: "subtle",
        icon: /* @__PURE__ */ jsx(Navigation20Regular, {}),
        "aria-label": t("shell.listPane.open"),
        "aria-keyshortcuts": "Alt+L",
        onClick: () => {
          store.getState().setListDrawerOpen(true);
        }
      }
    ) }),
    /* @__PURE__ */ jsxs("div", { className: styles.titles, children: [
      breadcrumb && breadcrumb.length > 0 && /* @__PURE__ */ jsx(Breadcrumb, { "aria-label": t("shell.breadcrumb"), size: "small", children: breadcrumb.map((entry, index) => /* @__PURE__ */ jsxs(Fragment2, { children: [
        index > 0 && /* @__PURE__ */ jsx(BreadcrumbDivider, {}),
        /* @__PURE__ */ jsx(BreadcrumbItem, { children: /* @__PURE__ */ jsx(
          BreadcrumbButton,
          {
            onClick: entry.onSelect,
            current: index === breadcrumb.length - 1,
            children: entry.label
          }
        ) })
      ] }, `${String(index)}-${entry.label}`)) }),
      /* @__PURE__ */ jsx("h1", { className: styles.title, children: title })
    ] }),
    actions && /* @__PURE__ */ jsx("div", { className: styles.actions, children: actions })
  ] });
}
function OverflowMenuItem({ tab, onSelect }) {
  const visible = useIsOverflowItemVisible(tab.value);
  if (visible) return null;
  return /* @__PURE__ */ jsx(
    MenuItem,
    {
      onClick: () => {
        onSelect(tab.value);
      },
      children: tab.label
    }
  );
}
function OverflowMenu({
  tabs,
  onSelect
}) {
  const styles = useStyles();
  const { t } = useTranslation("ui");
  const { ref, isOverflowing, overflowCount } = useOverflowMenu();
  if (!isOverflowing) return null;
  return /* @__PURE__ */ jsxs(Menu, { positioning: "below-end", children: [
    /* @__PURE__ */ jsx(MenuTrigger, { disableButtonEnhancement: true, children: /* @__PURE__ */ jsxs(
      MenuButton,
      {
        ref,
        appearance: "subtle",
        className: styles.moreButton,
        "aria-label": t("shell.tabsMore", { count: overflowCount }),
        children: [
          t("shell.tabsMoreShort"),
          /* @__PURE__ */ jsx("span", { className: styles.moreCount, "aria-hidden": "true", dir: "ltr", children: overflowCount })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(MenuPopover, { children: /* @__PURE__ */ jsx(MenuList, { children: tabs.map((tab) => /* @__PURE__ */ jsx(OverflowMenuItem, { tab, onSelect }, tab.value)) }) })
  ] });
}
function TabNav({ tabs, selectedValue, onSelect }) {
  const styles = useStyles();
  const segmented = useSegmentedTabsStyles();
  const { t } = useTranslation("ui");
  const shown = tabs.filter((tab) => tab.visible !== false);
  return /* @__PURE__ */ jsx("div", { className: styles.tabs, children: /* @__PURE__ */ jsx(Overflow, { padding: 48, minimumVisible: 1, children: /* @__PURE__ */ jsxs("div", { className: styles.overflow, children: [
    /* @__PURE__ */ jsx(
      TabList,
      {
        className: mergeClasses(segmented.list, styles.track),
        selectedValue,
        onTabSelect: (_e, data) => {
          onSelect(String(data.value));
        },
        "aria-label": t("shell.tabs"),
        children: shown.map((tab) => /* @__PURE__ */ jsx(
          OverflowItem,
          {
            id: tab.value,
            priority: tab.value === selectedValue ? 2 : 1,
            children: /* @__PURE__ */ jsx(Tab, { value: tab.value, className: segmented.tab, children: tab.label })
          },
          tab.value
        ))
      }
    ),
    /* @__PURE__ */ jsx(OverflowMenu, { tabs: shown, onSelect })
  ] }) }) });
}
function MainPane({ header, banner, tabs, children }) {
  const styles = useStyles();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    header,
    banner,
    tabs,
    /* @__PURE__ */ jsx("div", { className: styles.content, children })
  ] });
}
export {
  MainPane,
  PaneHeader,
  TabNav
};
//# sourceMappingURL=main-pane.js.map

import "/opt/batiflow/app/packages/ui/dist/shell/main-pane.griffel.css";